import { useLocation } from "react-router-dom";
import useLanguage from "./useLanguage.js";
import { routeLanguage } from "./useRouteLanguage.js";

/**
 * The language this page is being rendered in.
 *
 * One rule, and it is `routeLanguage`'s — the same one the i18next detector
 * applies on first paint and `useRouteLanguage` on every navigation after it:
 *
 *   - a URL that declares a language IS that language;
 *   - an unprefixed URL whose page HAS a localized version is the default one;
 *   - anything else defers to what i18next actually resolved.
 *
 * The second clause is what makes this honest rather than merely optimistic.
 * Without it this hook reported whatever i18next happened to hold, so a visitor
 * with Greek stored who landed on `/contact` was measured as Greek while being
 * shown English — a wrong number that nothing else would have contradicted.
 * The third keeps the guides index, which really can still render Greek at an
 * English URL, reported as what it is.
 *
 * This existed as the same expression in three places (the switcher, the
 * analytics context, and the homepage's head) before it was a hook. Three
 * copies of a language rule is three chances to disagree about what language a
 * page is in — and the head, the analytics event and the switcher disagreeing
 * is not a bug anyone would notice quickly.
 */
export default function usePageLanguage() {
  const { pathname } = useLocation();
  const { shortLanguage } = useLanguage();
  return routeLanguage(pathname) ?? shortLanguage;
}
